/*
Cold-start splash overlay — wordmark + rotating informational tip,
shown once per page-load over the map screen.

Rotation rules (mirror Android SplashTipsController.kt):
- First launch (splashIntroSeen=false): cycle through the ordered intro
  tips. After the splash closes, mark splashIntroSeen=true. Most users
  will only see 1–2 intros per session given the splash is brief — that's
  intentional, the goal is to expose them across the first few sessions.
- Subsequent launches: pick a random tip from the full bank, with
  player-snapshot placeholder substitution. Rotation cadence 5 s (down
  from Android's 7 s — PWA splashes are shorter overall).
- Tap-to-dismiss anywhere on the overlay.

Dismiss policy (mirror Android v1.3.0 "Réveil" — ColdStartSplash):
- Callers supply an `inAwaitPins` callback resolving when the map's
  arbres source is loaded AND at least one cluster/point is rendered
  in the viewport. The splash stays opaque until that resolves AND a
  kColdSplashMinMs floor has elapsed (no glitchy sub-second flash).
- kColdSplashMaxMs hard safety dismisses regardless — if the user's
  network stalls or the map fails, we never hold them hostage.
- Routes that don't mount the map use the fixed kFallbackDismissMs
  timer (degenerate case — they shouldn't see the splash at all).

Player snapshot is built once at mount (speciesCount, daysSinceFirst).
remarquableCount is NOT computed here because it would require parsing
the 32 MB GeoJSON; tips that reference it are simply filtered out from
random rotation. {captureCount} was retired in Android v1.3.0 — the
runtime placeholder set is now {speciesCount, remarquableCount,
daysSinceFirst}, kept in sync with SUPPORTED_PLACEHOLDERS upstream.
*/

import { ensureSplashTips, getIntroTips, getAllTips } from '../../data/splashTips'
import { isSplashIntroSeen, markSplashIntroSeen } from '../../data/onboardingStore'
import { listCaptures } from '../../data/captureRepository'
import { ensureDatasetStats } from '../../data/datasetStats'

const kFrenchNumber = new Intl.NumberFormat('fr-FR')

const kRotationMs: number = 5000  // cadence per tip
const kColdSplashMinMs: number = 2500  // floor: splash stays opaque for this long even if pins land sooner — parity Android COLD_SPLASH_MIN_MS
export const kFilterSplashMinMs: number = 1000  // shorter floor for the species-filtered splash — parity Android FILTER_SPLASH_MIN_MS
const kColdSplashMaxMs: number = 15_000  // safety: never let the splash hold the map hostage longer than this, even if pins never render
const kFallbackDismissMs: number = 2500  // when no `inAwaitPins` is supplied (e.g. dismiss-from-route-change), use the floor as a fixed timer
const kFadeOutMs: number = 320  // matches CSS transition duration for opacity

interface PlayerSnapshot
{
	speciesCount: number
	daysSinceFirst: number | null  // null if no captures yet
	remarquableCount: number | null  // intentionally null in the PWA splash (would need a 32 MB parse to fill)
}

/*
Inline SVG path silhouette platane — port direct du
`ic_arbre_canonical.xml` Android (viewport 24×24, un seul path). Tinté
via CSS `currentColor`. Inliner évite le flash d'un fetch séparé au
mount et garde l'ensemble dans le bundle JS gzippé. Partagé entre le
hero et les mini-platanes de la couronne.
*/
const kPlatanePath: string = 'M10.67,20 L10.67,14.67 C8.44,14.67 6.67,13.78 5.78,12 C4.89,11.56 4.89,9.78 5.33,8.44 C4.89,6.67 6.22,4.89 8,4.89 C8.44,3.56 10.22,2.67 12,2.67 C13.78,2.67 15.56,3.56 16,4.89 C17.78,4.89 19.11,6.67 18.67,8.44 C19.11,9.78 19.11,11.56 18.22,12 C17.33,13.78 15.56,14.67 13.33,14.67 L13.33,20 Z'

/*
Couronne décorative de 7 mini-platanes flottants autour du hero — port
direct d'Android `MiniArbreCrown` (`ui/map/MapOverlays.kt`). Arc
semi-circulaire AU-DESSUS du centre (pas de couronne 360°, la colonne
descendante wordmark/sous-titre/tip chevaucherait). Apparition
séquentielle (delays étalés 0-3000 ms) puis boucle infinie
fade/sway/drift, désynchronisée par platane via `phaseOffset`
(multiples irrationnels, évite les harmoniques). Toute l'animation est
en CSS (`.splash-mini`, 3 animations sur les propriétés transform
individuelles scale/rotate/translate) — le markup ne porte que les
paramètres par platane en custom properties.
*/
interface MiniArbre
{
	angleDeg: number     // 0° = vertical haut, -90° = gauche, +90° = droite
	radiusPx: number     // distance au centre du splash (1 dp Android = 1 px CSS)
	sizePx: number
	targetAlpha: number  // opacité au plateau du cycle
	delayMs: number      // décalage d'entrée dans le cycle 3500 ms
	phaseOffset: number  // désynchronise amplitude/signe du sway et du drift
}

const kMiniArbres: readonly MiniArbre[] = [
	{ angleDeg: -88, radiusPx: 130, sizePx: 22, targetAlpha: 0.50, delayMs: 0, phaseOffset: 0.00 },
	{ angleDeg: -55, radiusPx: 115, sizePx: 30, targetAlpha: 0.62, delayMs: 500, phaseOffset: 0.37 },
	{ angleDeg: -25, radiusPx: 140, sizePx: 18, targetAlpha: 0.45, delayMs: 1000, phaseOffset: 0.74 },
	{ angleDeg: 5, radiusPx: 155, sizePx: 26, targetAlpha: 0.55, delayMs: 1500, phaseOffset: 1.11 },
	{ angleDeg: 35, radiusPx: 120, sizePx: 32, targetAlpha: 0.65, delayMs: 2000, phaseOffset: 1.48 },
	{ angleDeg: 65, radiusPx: 135, sizePx: 20, targetAlpha: 0.48, delayMs: 2500, phaseOffset: 1.85 },
	{ angleDeg: 88, radiusPx: 125, sizePx: 28, targetAlpha: 0.58, delayMs: 3000, phaseOffset: 2.22 },
]

function renderCrownHtml(): string
{
	const vMinis = kMiniArbres.map((inMini) => {
		// Formules Android `MiniArbreItem` : position polaire depuis le
		// centre, amplitude de sway 5°-7° signée via sin(phaseOffset)
		// (certains platanes oscillent en opposition de phase), drift
		// vertical ±3 px signé via cos(phaseOffset × 1.3).
		const vAngleRad = (inMini.angleDeg - 90) * Math.PI / 180
		const vX = Math.cos(vAngleRad) * inMini.radiusPx
		const vY = Math.sin(vAngleRad) * inMini.radiusPx
		const vRotAmp = (5 + (inMini.phaseOffset % 2) * 2) * Math.sin(inMini.phaseOffset)
		const vDriftAmp = 3 * Math.cos(inMini.phaseOffset * 1.3)
		const vStyle = `--mx: ${vX.toFixed(1)}px; --my: ${vY.toFixed(1)}px;`
			+ ` --size: ${inMini.sizePx}px; --ta: ${inMini.targetAlpha};`
			+ ` --delay: ${inMini.delayMs}ms; --amp: ${vRotAmp.toFixed(2)}deg;`
			+ ` --drift: ${vDriftAmp.toFixed(2)}px`
		return `<div class="splash-mini" style="${vStyle}">`
			+ `<svg viewBox="0 0 24 24" width="100%" height="100%">`
			+ `<path fill="currentColor" d="${kPlatanePath}"/>`
			+ `</svg>`
			+ `</div>`
	}).join('')
	return `<div class="splash-crown" aria-hidden="true">${vMinis}</div>`
}

/*
HTML markup for the splash. Rendered into the host root by the caller
before `startSplash` is invoked. Uses data-open=true by default — the
splash is always shown when its markup is present.
*/
export function renderSplashHtml(inFilteredLabel?: string | null): string
{
	// Le wrapper sépare les transforms : sway sur le wrapper (rotation
	// infinite), fade-in + scale sur le SVG (run-once). Sans ça les deux
	// animations CSS se neutraliseraient l'une l'autre sur un seul élément.
	const vPlatane = `<div class="splash-hero-wrap" aria-hidden="true">`
		+ `<svg class="splash-hero" viewBox="0 0 24 24" width="168" height="168">`
		+ `<path fill="currentColor" d="${kPlatanePath}"/>`
		+ `</svg>`
		+ `</div>`
	// Couronne posée APRÈS le contenu pour que les mini-platanes passent
	// au-dessus du hero (z-order, parité Android SplashScaffold). Présente
	// sur les deux variantes, comme le SplashScaffold commun Android.
	const vCrown = renderCrownHtml()
	// Filtered mode (Android FilterSplash): hero + « Réveil des {pluriel}
	// parisiens » + spinner, no wordmark, no rotating tips. The plain cold
	// splash keeps its wordmark + tip slot.
	if (inFilteredLabel)
	{
		return renderSpinnerSplashHtml(
			`Réveil des <strong>${escapeHtml(pluralizeHead(inFilteredLabel))}</strong> parisiens`,
			vPlatane, vCrown)
	}
	return `
		<div class="splash" id="splash" data-open="true" role="status">
			<div class="splash-content">
				${vPlatane}
				<h1 class="display splash-wordmark">CanoPaname</h1>
				<div class="splash-subtitle" id="splash-subtitle"></div>
				<div class="splash-tip" id="splash-tip"></div>
			</div>
			${vCrown}
		</div>
	`
}

/*
Capture-transit splash — shown the instant the user validates their photo,
covering the camera-overlay teardown, the encode/GPS/save pipeline and (on a
first-of-species capture) the navigation into the species fiche. Same visual
family as the filtered splash (hero + phrase + spinner), different phrase.
*/
export function renderCaptureSplashHtml(): string
{
	const vPlatane = `<div class="splash-hero-wrap" aria-hidden="true">`
		+ `<svg class="splash-hero" viewBox="0 0 24 24" width="168" height="168">`
		+ `<path fill="currentColor" d="${kPlatanePath}"/>`
		+ `</svg>`
		+ `</div>`
	return renderSpinnerSplashHtml('Enregistrement de ta capture…', vPlatane, renderCrownHtml())
}

/* Markup commun des splashes « phrase + spinner » (filtré, capture). La
   phrase arrive en HTML déjà échappé par l'appelant. */
function renderSpinnerSplashHtml(inPhraseHtml: string, inPlataneHtml: string, inCrownHtml: string): string
{
	return `
		<div class="splash" id="splash" data-open="true" role="status">
			<div class="splash-content">
				${inPlataneHtml}
				<div class="splash-filter-phrase">${inPhraseHtml}</div>
				<div class="splash-filter-spinner" aria-hidden="true"></div>
			</div>
			${inCrownHtml}
		</div>
	`
}

/*
Pluralise the head word of a French vernacular name — "Platane" →
"Platanes", "Bouleau" → "Bouleaux", "Cyprès" → "Cyprès" (invariant),
"Tilleul à petites feuilles" → "Tilleuls à petites feuilles". Adjective
agreement isn't handled ("Chêne vert" → "Chênes vert") — assumed, the
banner that follows gives the exact name. Port direct d'Android
`pluralizeHead`.
*/
function pluralizeHead(inLabel: string): string
{
	const vSpace = inLabel.indexOf(' ')
	const vHead = vSpace === -1 ? inLabel : inLabel.slice(0, vSpace)
	const vRest = vSpace === -1 ? '' : inLabel.slice(vSpace)
	const vLower = vHead.toLowerCase()
	const vPluralHead = (vLower.endsWith('s') || vLower.endsWith('x') || vLower.endsWith('z'))
		? vHead
		: (vLower.endsWith('eau') || vLower.endsWith('eu'))
			? vHead + 'x'
			: vHead + 's'
	return vPluralHead + vRest
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

/*
Start the rotation timer and gate the dismiss on actual map readiness.
Returns a controller exposing `dismiss()` for callers that want to
close early (e.g. on a route change).

Dismiss policy (mirror Android v1.3.0 "Réveil" — ColdStartSplash):
- If `inAwaitPins` is supplied, the splash stays opaque until the
  promise resolves (pins are rendered) AND at least kColdSplashMinMs
  have elapsed since mount. A safety timer dismisses at
  kColdSplashMaxMs regardless, so the splash never holds the map
  hostage. Tip rotation runs during the wait — that's its purpose.
- If `inAwaitPins` is omitted, the splash uses a fixed
  kFallbackDismissMs timer (used by routes that don't mount the map).

The splash fires on EVERY mount of MapScreen — cold start AND in-app
navigation back from Arboretum / Profile / Species (parité Android,
which replays its « Réveil des arbres » splash on each map entry).
On cold start it covers the 28 MB GeoJSON fetch + parse; on remounts
the persistent map resolves the wait instantly, so the splash is a
brief branded transition. Do NOT add a session guard to suppress the
remount splash — that divergence was tried and reverted.
*/
export async function startSplash(
	inRoot: HTMLElement,
	inAwaitPins?: () => Promise<boolean>,
	inOptions?: { floorMs?: number; skipTips?: boolean },
): Promise<{ dismiss: () => void }>
{
	const vSplash = inRoot.querySelector<HTMLElement>('#splash')
	if (!vSplash) return { dismiss: () => { /* already gone */ } }

	// Filtered mode passes a shorter floor (kFilterSplashMinMs) + skipTips —
	// the phrase + spinner are baked into renderSplashHtml, there's no
	// #splash-tip slot to rotate.
	const vFloorMs = inOptions?.floorMs ?? kColdSplashMinMs
	const vSkipTips = inOptions?.skipTips === true
	const vMountTs: number = performance.now()  // baseline for the floor delay
	let vRotationTimer: number | null = null  // setInterval id for tip rotation
	let vSafetyTimer: number | null = null  // setTimeout id for the absolute max safety dismiss
	let vDismissed: boolean = false  // single-fire guard

	const vDismiss = (): void => {
		if (vDismissed) return
		vDismissed = true
		if (vRotationTimer !== null) window.clearInterval(vRotationTimer)
		if (vSafetyTimer !== null) window.clearTimeout(vSafetyTimer)
		hideAndRemove(vSplash)
	}

	// Tap anywhere on the overlay to dismiss early. The user gets out
	// instantly, foregoing the wait-for-pins guard intentionally — they've
	// signalled they want to interact with the map now.
	vSplash.addEventListener('click', vDismiss)

	// Sous-titre fixe « Réveil des 217 264 arbres parisiens » — parité
	// Android ColdStartSplash (compteur = totalArbres du dataset, qui
	// matche le point_count du cluster dezoomé à fond). Rempli async,
	// le slot réserve sa hauteur via min-height.
	if (!vSkipTips)
	{
		void ensureDatasetStats().then((inStats) => {
			const vEl = vSplash.querySelector<HTMLElement>('#splash-subtitle')
			if (vEl && typeof inStats.totalArbres === 'number' && inStats.totalArbres > 0)
			{
				vEl.textContent = `Réveil des ${kFrenchNumber.format(inStats.totalArbres)} arbres parisiens`
			}
		}).catch(() => { /* offline cold start sans l'asset — sous-titre omis */ })
	}

	// Filtered splash skips the whole tip pipeline — its phrase + spinner
	// are static markup, no #splash-tip slot, no rotation timer.
	if (!vSkipTips) try
	{
		await ensureSplashTips()
		const vIntroSeen = await isSplashIntroSeen()
		const vSnap = await buildPlayerSnapshot()  // speciesCount, etc., for placeholder substitution

		const vSequence = pickSequence(vIntroSeen, vSnap)  // ordered list of tip TEXT (already substituted)
		if (vSequence.length > 0)
		{
			let vIndex = 0
			paintTip(vSplash, vSequence[vIndex])
			if (vSequence.length > 1)
			{
				vRotationTimer = window.setInterval(() => {
					vIndex = (vIndex + 1) % vSequence.length
					paintTip(vSplash, vSequence[vIndex])
				}, kRotationMs)
			}

			// Mark intro seen once the splash is up — even if the user dismisses
			// after one tip, we've satisfied the "saw it once" gate.
			if (!vIntroSeen) void markSplashIntroSeen().catch(() => { /* non-critical */ })
		}
	}
	catch (vErr)
	{
		console.warn('Splash tips boot failed', vErr)
		// Continue with the wordmark-only splash — pin-await still drives the dismiss.
	}

	// Schedule the safety upper bound BEFORE awaiting pins. If the
	// browser stalls or `inAwaitPins` rejects silently, we still close.
	vSafetyTimer = window.setTimeout(vDismiss, inAwaitPins ? kColdSplashMaxMs : kFallbackDismissMs)

	if (inAwaitPins)
	{
		void (async () => {
			try { await inAwaitPins() }
			catch (vErr) { console.warn('Splash pin-await rejected', vErr) }
			// Honor the minimum-display floor: pins may land in 800 ms on a
			// warm reload, but flashing the splash off that quickly feels
			// like a glitch. 2500 ms is the Android parity floor.
			const vElapsed = performance.now() - vMountTs
			const vRemaining = Math.max(0, vFloorMs - vElapsed)
			if (vRemaining > 0) await new Promise<void>((inR) => window.setTimeout(inR, vRemaining))
			vDismiss()
		})()
	}

	return { dismiss: vDismiss }
}

/*
Build the ordered sequence of tip texts to display. Intro mode emits the
intro list verbatim (ordered). Random mode picks up to 3 distinct tips
from the full bank that pass placeholder substitution — enough to cover
the auto-dismiss window if the user lingers.
*/
function pickSequence(inIntroSeen: boolean, inSnap: PlayerSnapshot): string[]
{
	if (!inIntroSeen)
	{
		return getIntroTips()
			.map((inT) => substitute(inT.text, inSnap))
			.filter((inS): inS is string => inS !== null)
	}

	const vAll = getAllTips()
	const vShuffled = shuffle(vAll)  // copy + Fisher-Yates
	const vOut: string[] = []
	for (const vTip of vShuffled)
	{
		if (vOut.length >= 3) break  // cap — kMaxDisplayMs / kRotationMs ≈ 1; 3 is generous slack
		const vText = substitute(vTip.text, inSnap)
		if (vText !== null) vOut.push(vText)
	}
	return vOut
}

/*
Substitute `{name}` placeholders in `inText` using the player snapshot.
Returns null if any placeholder maps to a missing value — the caller
treats this as "skip this tip" so the user never sees a dangling `{xxx}`.
*/
function substitute(inText: string, inSnap: PlayerSnapshot): string | null
{
	let vMissing = false  // becomes true when any placeholder can't be resolved
	const vResult = inText.replace(/\{(\w+)\}/g, (_, inKey: string) => {
		const vValue = (inSnap as unknown as Record<string, unknown>)[inKey]
		if (vValue === null || vValue === undefined)
		{
			vMissing = true
			return ''
		}
		return String(vValue)
	})
	return vMissing ? null : vResult
}

/*
Build the player snapshot used for placeholder substitution. Cheap —
just one IDB read + a sort.
*/
async function buildPlayerSnapshot(): Promise<PlayerSnapshot>
{
	try
	{
		const vCaptures = await listCaptures()  // already chronological
		const vSpecies = new Set<number>()  // distinct speciesIndex values
		for (const vCap of vCaptures) vSpecies.add(vCap.speciesIndex)
		const vFirst = vCaptures.length > 0 ? vCaptures[0].timestamp : null  // earliest capture timestamp
		const vDaysSinceFirst = vFirst !== null
			? Math.max(1, Math.floor((Date.now() - vFirst) / (24 * 3600 * 1000)))
			: null
		return {
			speciesCount: vSpecies.size,
			daysSinceFirst: vDaysSinceFirst,
			remarquableCount: null,
		}
	}
	catch
	{
		return { speciesCount: 0, daysSinceFirst: null, remarquableCount: null }
	}
}

function shuffle<T>(inArr: readonly T[]): T[]
{
	const vOut = [...inArr]  // shallow copy; we don't mutate the caller's array
	for (let vI = vOut.length - 1; vI > 0; vI--)
	{
		const vJ = Math.floor(Math.random() * (vI + 1))
		;[vOut[vI], vOut[vJ]] = [vOut[vJ], vOut[vI]]
	}
	return vOut
}

function paintTip(inSplash: HTMLElement, inText: string): void
{
	const vEl = inSplash.querySelector<HTMLElement>('#splash-tip')
	if (!vEl) return
	// Quick fade-out / fade-in on rotate so changes are gentle. Done via
	// data-fading toggle so CSS owns the transitions.
	vEl.dataset.fading = 'true'
	window.setTimeout(() => {
		vEl.textContent = inText
		vEl.dataset.fading = 'false'
	}, 180)
}

function hideAndRemove(inSplash: HTMLElement): void
{
	inSplash.setAttribute('data-open', 'false')
	window.setTimeout(() => { inSplash.remove() }, kFadeOutMs)
}
